import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import type { PublicUser } from "@scenix/core";
import { useSession } from "../lib/session";

/**
 * The account menu behind the avatar in the header.
 *
 * A menu rather than a row of links, because the header should carry the one
 * thing you look at (which account am I) and not the four things you
 * occasionally need.
 */
export function AccountMenu({ user }: { user: PublicUser }) {
  const { signOut } = useSession();
  const container = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  // The menu remembers where it was opened, and is open only while still
  // there. Navigating therefore closes it without an effect that syncs state
  // to a route change, which would cascade a second render on every
  // navigation. Following a link would otherwise leave the menu hanging over
  // the page it just went to.
  const [openedAt, setOpenedAt] = useState<string | null>(null);
  const open = openedAt !== null && openedAt === location.pathname;

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpenedAt(null);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpenedAt(null);
      // Focus goes back where it came from, or a keyboard user is left at the
      // top of the document with no idea what happened.
      button.current?.focus();
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="account" ref={container}>
      <button
        type="button"
        ref={button}
        className="account-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account: ${user.displayName}`}
        onClick={() => setOpenedAt(open ? null : location.pathname)}
      >
        <Avatar user={user} />
      </button>

      {open && (
        <div className="account-menu" role="menu">
          <div className="account-who">
            <strong>{user.displayName}</strong>
            <span>{user.email}</span>
          </div>

          <Link to="/settings" role="menuitem">
            Settings
          </Link>
          <Link to="/settings/keys" role="menuitem">
            Agent keys
          </Link>
          <Link to="/settings/about" role="menuitem">
            This deployment
          </Link>

          <button type="button" role="menuitem" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/** The picture, or initials when Google gave us nothing. */
function Avatar({ user }: { user: PublicUser }) {
  const [broken, setBroken] = useState(false);

  if (user.avatarUrl && !broken) {
    return (
      <img
        src={user.avatarUrl}
        alt=""
        width={28}
        height={28}
        // Google's avatar host refuses requests that carry a referrer, and the
        // result is a broken image rather than an error anybody sees.
        referrerPolicy="no-referrer"
        onError={() => setBroken(true)}
      />
    );
  }

  return <span aria-hidden="true">{initials(user.displayName || user.email)}</span>;
}

/**
 * One or two letters from a name.
 *
 * Split on whitespace rather than taking the first two characters, so that
 * "Masahji Stewart" reads MS and not MA. Exported for SpaceAvatar, which wants
 * the same fallback treatment for a space with no logo.
 */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}
